import { Box, Flex, Table, Text } from '@chakra-ui/react'
import { Users } from 'lucide-react'
import React from 'react'

import { useReportingI18n } from '../../../i18n'
import type {
  QueueAgent,
  QueueDashboardAgentActions,
  QueueDashboardCustomStatus,
  QueueDashboardExtension,
} from '../types'
import AgentRow from './AgentRow'

export interface ActiveAgentsTableProps {
  agents: QueueAgent[]
  isLoading: boolean
  customStatuses?: QueueDashboardCustomStatus[]
  extensions?: QueueDashboardExtension[]
  showActions?: boolean
  agentActions?: QueueDashboardAgentActions
}

const Thead = Table.Header
const Tbody = Table.Body
const Tr = Table.Row
const Th = Table.ColumnHeader

const tableHeader = (title: string, shrink?: boolean) => {
  return (
    <Th
      padding="12px 16px"
      bg="gray.subtle"
      color="fg.default"
      fontSize="0.75rem"
      fontWeight="600"
      textTransform="none"
      width={shrink ? '1%' : undefined}
      whiteSpace={shrink ? 'nowrap' : undefined}
    >
      {title}
    </Th>
  )
}

const EmptyState: React.FC = () => {
  const { text } = useReportingI18n()

  return (
    <Flex
      direction="column"
      alignItems="center"
      justifyContent="center"
      padding="48px"
      color="fg.muted"
      data-testid="active-agents-table-empty-state"
    >
      <Flex
        alignItems="center"
        justifyContent="center"
        width="80px"
        height="80px"
        borderRadius="full"
        bg="bg.muted"
        mb="16px"
      >
        <Box as={Users} width="36px" height="36px" color="fg.muted" />
      </Flex>
      <Text fontSize="1rem" fontWeight="600" color="fg.default">
        {text(
          'queueDashboard.activeAgentsTable.emptyState.title',
          'No Agents found',
        )}
      </Text>
      <Text fontSize="0.875rem" mt="4px" color="fg.muted">
        {text(
          'queueDashboard.activeAgentsTable.emptyState.description',
          'There is currently no information showing for the current Queue',
        )}
      </Text>
    </Flex>
  )
}

const ActiveAgentsTable: React.FC<ActiveAgentsTableProps> = ({
  agents,
  isLoading,
  customStatuses = [],
  extensions = [],
  showActions = false,
  agentActions,
}) => {
  const { text } = useReportingI18n()

  if (!isLoading && agents.length === 0) {
    return <EmptyState />
  }

  return (
    <Table.Root variant="line" size="sm">
      <Thead>
        <Tr>
          {tableHeader(
            text('queueDashboard.activeAgentsTable.agentName', 'Agent Name'),
          )}
          {tableHeader(text('queueDashboard.activeAgentsTable.ext', 'Ext'))}
          {tableHeader(
            text('queueDashboard.activeAgentsTable.loggedIn', 'Logged In'),
          )}
          {tableHeader(
            text('queueDashboard.activeAgentsTable.talkTime', 'Talk Time'),
          )}
          {tableHeader(
            text('queueDashboard.activeAgentsTable.answered', 'Answered'),
          )}
          {tableHeader(
            text('queueDashboard.activeAgentsTable.missed', 'Missed'),
          )}
          {tableHeader(
            text('queueDashboard.activeAgentsTable.status', 'Status'),
          )}
          {tableHeader(
            text(
              'queueDashboard.activeAgentsTable.lastOffered',
              'Last Offered',
            ),
          )}
          {showActions &&
            tableHeader(
              text('queueDashboard.activeAgentsTable.action', 'Action'),
              true,
            )}
        </Tr>
      </Thead>
      <Tbody>
        {agents.map((agent, index) => (
          <AgentRow
            key={agent.extension}
            agent={agent}
            isActive={true}
            index={index}
            customStatuses={customStatuses}
            extensions={extensions}
            showActions={showActions}
            agentActions={agentActions}
          />
        ))}
      </Tbody>
    </Table.Root>
  )
}

export default ActiveAgentsTable
